/**
 * Firebase Error Message Mapping
 * Maps Firebase auth error codes to user-friendly messages
 */

export const FIREBASE_ERROR_MESSAGES = {
  // Authentication errors
  "auth/user-not-found": "Email doesn't exist",
  "auth/wrong-password": "Incorrect password.",
  "auth/invalid-credential":
    "Invalid email or password. Please check your credentials.",
  "auth/invalid-email": "Please enter a valid email.",
  "auth/user-disabled":
    "This account has been disabled. Please contact support.",
  "auth/too-many-requests": "Too many failed attempts. Please try again later.",

  // Registration errors
  "auth/email-already-in-use": "Email already in use. Try signing in instead.",
  "auth/weak-password":
    "Password is too weak. Please choose a stronger password.",
  "auth/operation-not-allowed":
    "Email/password accounts are not enabled. Please contact support.",

  // Password reset errors
  // "auth/user-not-found": "No account found with this email address.",
  // "auth/invalid-email": "Please enter a valid email address.",
  // "auth/too-many-requests":
  //   "Too many reset requests. Please wait before trying again.",

  // Email verification errors
  "auth/invalid-action-code":
    "The verification link is invalid or has expired.",
  "auth/expired-action-code":
    "The verification link has expired. Please request a new one.",
  "auth/invalid-continue-uri": "Invalid verification link. Please try again.",
  "auth/email-not-verified":
    "Please verify your email before signing in. Check your inbox for the verification link.",
  "auth/missing-email": "Please enter your email to continue signing in.",
  "auth/argument-error":
    "This sign-in link is invalid. Please request a new one.",

  // Network and general errors
  "auth/network-request-failed":
    "Network error. Please check your internet connection.",
  "auth/internal-error": "Something went wrong. Please try again.",
  "auth/quota-exceeded": "Too many requests. Please try again later.",

  // Google Sign-in errors
  "auth/popup-closed-by-user": "Sign-in was cancelled. Please try again.",
  "auth/popup-blocked":
    "Pop-up was blocked. Please allow pop-ups and try again.",
  "auth/cancelled-popup-request": "Sign-in was cancelled. Please try again.",
  "auth/account-exists-with-different-credential":
    "An account already exists with the same email but different sign-in method.",

  // Default error
  default: "Something went wrong. Please try again.",
};

/**
 * Get user-friendly error message from Firebase error
 * @param {Error} error - Firebase error object
 * @returns {string} User-friendly error message
 */
export const getFirebaseErrorMessage = (error) => {
  if (!error) return "";

  // Extract error code from Firebase error
  const errorCode = error.code || error.message;

  // Return mapped message or default
  return FIREBASE_ERROR_MESSAGES[errorCode] || FIREBASE_ERROR_MESSAGES.default;
};

/**
 * Check if error is a network/connectivity issue
 * @param {Error} error - Firebase error object
 * @returns {boolean} True if network error
 */
export const isNetworkError = (error) => {
  const networkErrorCodes = [
    "auth/network-request-failed",
    "auth/timeout",
    "auth/too-many-requests",
  ];

  return networkErrorCodes.includes(error?.code);
};

/**
 * Check if error allows retry
 * @param {Error} error - Firebase error object
 * @returns {boolean} True if user can retry
 */
export const canRetryError = (error) => {
  const retryableErrors = [
    "auth/network-request-failed",
    "auth/too-many-requests",
    "auth/internal-error",
    "auth/popup-closed-by-user",
    "auth/popup-blocked",
  ];

  return retryableErrors.includes(error?.code);
};
